import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download } from "lucide-react";
import { api, errMsg, num, dt, today } from "@/lib/api";
import { PageHeader, Card, Table, Sel, Money, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const Result = ({ r }) => (
  <Card title={`Hasil impor ${r.code}`} className="mb-4" testid="import-result">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 text-sm">
      <div>Baris: <b className="num">{r.row_count}</b></div><div>Qty terjual: <b className="num">{num(r.total_qty)}</b></div>
      <div>Penjualan: <Money v={r.total_sales} className="font-bold" /></div><div>Konsumsi teoritis (COGS): <Money v={r.cogs} className="font-bold" /></div>
    </div>
    {[["Produk tanpa resep", r.unmapped.map((u) => `${u.product} (${u.qty})`), "text-amber-700"], ["Peringatan", r.warnings, "text-rose-700"], ["Baris dilewati", r.errors, "text-slate-600"]].map(([t, list, c]) => list?.length ? (
      <div key={t} className={`px-4 pb-2 text-xs ${c}`}><b>{t}:</b> {list.join(" · ")}</div>) : null)}
    <Table testid="consumption-table" rows={r.consumption} cols={[
      { h: "Outlet", k: "location_name" }, { h: "Barang", k: "item_name" },
      { h: "Konsumsi teoritis", right: true, r: (x) => <span className="num font-semibold">{num(x.qty)} {x.base_unit}</span> },
      { h: "Nilai", right: true, r: (x) => <Money v={x.value} /> }]} />
  </Card>
);

export default function SalesImport() {
  const { outlets } = useAuth();
  const ref = useRef();
  const [loc, setLoc] = useState("");
  const [res, setRes] = useState(null);
  const [hist, setHist] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/sales/imports").then((r) => setHist(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("location_id", loc);
    try { const { data } = await api.post("/sales/import", fd); setRes(data); toast.success("Penjualan diimpor, konsumsi diposting"); load(); } catch (er) { toast.error(errMsg(er)); }
    e.target.value = "";
  };
  const sample = () => {
    const csv = `date,outlet,order_id,product,qty,amount\n${today()},MONA1,POS-${Date.now() % 100000},Mona Signature Chocolate,80,2800000\n`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "contoh_penjualan_pos.csv";
    a.click();
  };
  const reverse = async (h) => { try { await api.post(`/sales/imports/${h.id}/reverse`); toast.success("Impor dibatalkan (reversal)"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader title="Impor penjualan POS" sub="Unggah log penjualan (CSV/Excel). Sistem memetakan produk ke resep dan memposting konsumsi bahan teoritis dari stok outlet.">
        <button onClick={sample} className="h-9 px-3 rounded-md border bg-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="download-sample-button"><Download size={15} /> Contoh CSV</button>
      </PageHeader>
      <Card className="p-4 mb-4">
        <div className="grid md:grid-cols-[240px_1fr] gap-3 items-end">
          <div><div className="text-[11px] font-semibold uppercase text-slate-500 mb-1">Outlet (jika tidak ada kolom outlet)</div>
            <Sel testid="import-location-select" value={loc} onChange={setLoc} placeholder="Ambil dari file" options={outlets.map((o) => ({ value: o.id, label: o.name }))} /></div>
          <div>
            <input ref={ref} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} data-testid="import-file-input" />
            <button onClick={() => ref.current.click()} className="w-full h-12 border-2 border-dashed rounded-lg flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 hover:border-emerald-600 hover:text-emerald-700" data-testid="import-upload-button"><Upload size={17} /> Pilih file CSV / Excel</button>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">Kolom: <code>date, outlet, order_id, product, qty, amount</code>. Outlet bisa berupa kode (MONA1) atau nama. File/order yang sama tidak bisa diimpor dua kali.</div>
      </Card>
      {res && <Result r={res} />}
      <Card title="Riwayat impor">
        <Table testid="import-history" rows={hist} onRow={setOpen} cols={[
          { h: "Kode", r: (h) => <span className="num text-xs font-semibold">{h.code}</span> }, { h: "File", k: "filename" }, { h: "Outlet", r: (h) => h.locations.join(", ") },
          { h: "Baris", right: true, k: "row_count" }, { h: "Penjualan", right: true, r: (h) => <Money v={h.total_sales} /> }, { h: "COGS teoritis", right: true, r: (h) => <Money v={h.cogs} /> },
          { h: "Oleh", r: (h) => <span className="text-xs">{h.imported_by.name} · {dt(h.imported_at)}</span> }, { h: "Status", r: (h) => <StatusBadge s={h.status} /> },
          { h: "", r: (h) => h.status === "posted" ? <button onClick={(e) => { e.stopPropagation(); reverse(h); }} className="text-xs text-rose-600 hover:underline" data-testid={`reverse-import-${h.code}`}>Batalkan</button> : null },
        ]} />
      </Card>
      {open && <div className="mt-4"><Result r={open} /></div>}
    </div>
  );
}
